import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatNumber } from '@/components/sizing/advancedInsightsShared';

type BottleneckComparisonChartProps = {
  promptGpus: number;
  decodeGpus: number;
  kvGpus: number;
  limitingConstraint: 'prompt' | 'decode' | 'kv' | 'none';
};

export const BottleneckComparisonChart: React.FC<BottleneckComparisonChartProps> = ({
  promptGpus,
  decodeGpus,
  kvGpus,
  limitingConstraint,
}) => {
  const chartData = useMemo(() => {
    const maxGpus = Math.max(promptGpus, decodeGpus, kvGpus);
    
    return [
      {
        name: 'Prompt Throughput',
        gpus: promptGpus,
        isBottleneck: limitingConstraint === 'prompt',
        utilization: maxGpus > 0 ? (promptGpus / maxGpus) * 100 : 0,
      },
      {
        name: 'Decode Throughput',
        gpus: decodeGpus,
        isBottleneck: limitingConstraint === 'decode',
        utilization: maxGpus > 0 ? (decodeGpus / maxGpus) * 100 : 0,
      },
      {
        name: 'KV Memory',
        gpus: kvGpus,
        isBottleneck: limitingConstraint === 'kv',
        utilization: maxGpus > 0 ? (kvGpus / maxGpus) * 100 : 0,
      },
    ];
  }, [promptGpus, decodeGpus, kvGpus, limitingConstraint]);

  const getBarColor = (isBottleneck: boolean) => {
    return isBottleneck ? 'hsl(var(--destructive))' : 'hsl(var(--primary))';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">GPU Requirements by Constraint</CardTitle>
        <CardDescription>Comparison of GPU requirements across throughput and memory constraints</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="name" 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(val) => formatNumber(val)}
              label={{ value: 'GPUs Required', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
              formatter={(value: number, name: string, props: any) => [
                `${formatNumber(value)} GPUs (${props.payload.utilization.toFixed(1)}% utilization)`,
                props.payload.isBottleneck ? 'Bottleneck' : 'Requirement',
              ]}
            />
            <Bar dataKey="gpus" radius={[8, 8, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.isBottleneck)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        
        <div className="mt-4 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: 'hsl(var(--primary))' }} />
            <span className="text-muted-foreground">Non-limiting constraint</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: 'hsl(var(--destructive))' }} />
            <span className="text-muted-foreground">Bottleneck (determines final sizing)</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
